import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Tooltip from '@mui/material/Tooltip';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { GridActionsCellItem } from '@mui/x-data-grid';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { buildPath, nameof } from '@e-socna/utils';

import { PATH_RESOLVABLE_SERVICE_DEFAULT } from '../../app/constants/paths.constant';
import type { ReferencesContext_GenericStatus } from '../../contexts/ReferencesContext';

import { STATUS_ID } from './constants/data-grid.constant';
import type { ResolvableServiceListCmp_ResolvableService } from './hooks/useFindAllResolvableServicesQuery.hook';

interface Props {
  readonly resolvableService: ResolvableServiceListCmp_ResolvableService;
}

const Action = ({ resolvableService }: Readonly<Props>) => {
  const navigate = useNavigate();

  const consult = useCallback(() => {
    const path = buildPath(PATH_RESOLVABLE_SERVICE_DEFAULT, {
      id: resolvableService._id,
    });
    void navigate(path);
  }, [navigate, resolvableService._id]);

  const actionLabel = 'Consulter le Service';

  return (
    <GridActionsCellItem
      icon={
        <Tooltip title={actionLabel}>
          <VisibilityIcon />
        </Tooltip>
      }
      label={actionLabel}
      onClick={consult}
    />
  );
};

export const getColumns = (
  genericStatuses: readonly ReferencesContext_GenericStatus[],
): readonly GridColDef<ResolvableServiceListCmp_ResolvableService>[] => [
  {
    field: 'actions',
    headerName: 'Actions',
    description: 'Actions',
    editable: false,
    sortable: false,
    type: 'actions',
    getActions: (
      params: Readonly<
        GridRowParams<ResolvableServiceListCmp_ResolvableService>
      >,
    ) => [<Action key="consult" resolvableService={params.row} />],
  },
  {
    field: nameof<ResolvableServiceListCmp_ResolvableService>('code'),
    headerName: 'Code',
    width: 350,
    description: 'Code',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: nameof<ResolvableServiceListCmp_ResolvableService>('label'),
    headerName: 'Libellé',
    width: 400,
    description: 'Libellé',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: nameof<ResolvableServiceListCmp_ResolvableService>('title'),
    headerName: 'Titre',
    width: 600,
    description: 'Titre',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: STATUS_ID,
    headerName: 'Statut',
    width: 200,
    description: 'Statut',
    editable: false,
    sortable: true,
    hideable: true,
    type: 'singleSelect',
    valueGetter: (
      _: unknown,
      row: ResolvableServiceListCmp_ResolvableService,
    ) =>
      genericStatuses.find(
        (genericStatus) => genericStatus._id === row.statusId,
      )?.code ?? 'Inconnu',
    valueOptions: genericStatuses.map((genericStatus) => ({
      value: genericStatus.code,
      label: genericStatus.label,
    })),
  },
];
